import React, { startTransition } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Box,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nCommonNs } from '@/i18n/dictionaries/interfaces.ts';
const PrivacyPolicy = React.lazy(() => import('./PrivacyPolicy.tsx'));
const DataProcessingAddendum = React.lazy(() => import('./DataProcessingAddendum.tsx'));
const TermsAndConditions = React.lazy(() => import('./TermsAndConditions.tsx'));
const CertificationsTab = React.lazy(() => import('./CertificationsTab.tsx'));

interface LegalTextDialogProps {
  open: boolean;
  onClose: () => void;
  initialTab?: 'privacy' | 'dpa' | 'terms' | 'certifications';
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`legal-tabpanel-${index}`}
      aria-labelledby={`legal-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function LoadingFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
      <CircularProgress />
    </Box>
  );
}

const LegalTextDialog: React.FC<LegalTextDialogProps> = ({
  open,
  onClose,
  initialTab = 'privacy',
}) => {
  const [translate] = useTranslation(I18nNamespace.Common);
  const getInitialTabIndex = () => {
    if (initialTab === 'dpa') return 1;
    if (initialTab === 'terms') return 2;
    if (initialTab === 'certifications') return 3;
    return 0;
  };
  const [value, setValue] = React.useState(getInitialTabIndex());

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    startTransition(() => {
      setValue(newValue);
    });
  };

  React.useEffect(() => {
    if (open) {
      setValue(getInitialTabIndex());
    }
  }, [open, initialTab]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {translate(nameof.full<I18nCommonNs>((n) => n.legalCenter.title))}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={value} onChange={handleChange} aria-label="legal documents tabs">
            <Tab label={translate(nameof.full<I18nCommonNs>((n) => n.legalCenter.privacyPolicy))} id="legal-tab-0" aria-controls="legal-tabpanel-0" />
            <Tab label={translate(nameof.full<I18nCommonNs>((n) => n.legalCenter.dpa))} id="legal-tab-1" aria-controls="legal-tabpanel-1" />
            <Tab label={translate(nameof.full<I18nCommonNs>((n) => n.legalCenter.termsAndConditions))} id="legal-tab-2" aria-controls="legal-tabpanel-2" />
            <Tab label={translate(nameof.full<I18nCommonNs>((n) => n.legalCenter.certifications))} id="legal-tab-3" aria-controls="legal-tabpanel-3" />
          </Tabs>
        </Box>
        <Box sx={{ px:3, pb: 2, whiteSpace: 'pre-line' }}>
          <TabPanel value={value} index={0}>
            {value === 0 && (
              <React.Suspense fallback={<LoadingFallback />}>
                <PrivacyPolicy />
              </React.Suspense>
            )}
          </TabPanel>
          <TabPanel value={value} index={1}>
            {value === 1 && (
              <React.Suspense fallback={<LoadingFallback />}>
                <DataProcessingAddendum />
              </React.Suspense>
            )}
          </TabPanel>
          <TabPanel value={value} index={2}>
            {value === 2 && (
              <React.Suspense fallback={<LoadingFallback />}>
                <TermsAndConditions />
              </React.Suspense>
            )}
          </TabPanel>
          <TabPanel value={value} index={3}>
            {value === 3 && (
              <React.Suspense fallback={<LoadingFallback />}>
                <CertificationsTab />
              </React.Suspense>
            )}
          </TabPanel>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
          {translate(nameof.full<I18nCommonNs>((n) => n.buttonLabels.close))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LegalTextDialog;
